import React, { useCallback, useEffect } from 'react';

import { useTheme } from '@material-ui/core';
import { getCertificates } from '../../services/learner.service';
import { getIndividualCertificate, downloadCertificatesInCombinedPDF } from '../../../../../util/APIUtils';
import store, { changeToastType, showToast } from '../../../../core/store';
import {
  MAX_CERTIFICATES_DOWNLOAD_LIMIT,
  SNACK_BAR_ERROR,
  SUCCESS,
} from '../../../../services/constants';
import { LinkButton } from '@orijinworks/frontend-commons';
import CustomDatePicker from '../../../../../components/users/datatable/customDatePicker';
import { convertBase64ToBlob, downloadBlobFile } from '../../../../services/utilty';

export const useCertificatesHook = (props) => {
  const { user } = props;
  const userDetails = user?.userDetail;

  const [isLoading, setLoading] = React.useState(false);
  const [selectedCertificates, setCertificates] = React.useState([]);
  const [certificatesData, setCertificatesData] = React.useState([]);
  const theme = useTheme();

  const tableOptions = {
    selection: true,
    search: false,
    filtering: true,
    pageSize: 20,
    toolbar: !!selectedCertificates.length,
  };

  const columns = [
    {
      field: 'resourceName',
      title: 'Program',
      filtering: true,
    },
    {
      field: 'issuedDate',
      title: 'Completion Date',
      type: 'date',
      defaultSort: 'desc',
      filterComponent: (props) => <CustomDatePicker format={'dd/MM/yyyy'} {...props} isReadyOnly={true} />,
    },
    {
      field: 'action',
      title: 'Action',
      filtering: false,
      sorting: false,
      render: ({ resourceKey, personKey }) => (
        <LinkButton
          style={{ color: theme.palette.primary.navy }}
          id={`link-button-${personKey}`}
          onClick={() => onDownloadCertificate(resourceKey, personKey)}
        >
          Download
        </LinkButton>
      ),
    },
  ];

  const fetchCertificates = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getCertificates(userDetails?.userId);

      setCertificatesData(response);
    } catch (error) {
      console.error('Error fetching certificates:', error);
    } finally {
      setLoading(false);
    }
  }, [userDetails?.userId]);

  useEffect(() => {
    if (user) {
      fetchCertificates();
    }
  }, [user, fetchCertificates]);

  const onDownloadCertificate = async (resourceKey, personKey) => {
    setLoading(true);
    try {
      const { status, payload } = await getIndividualCertificate(parseInt(resourceKey), parseInt(personKey));
      if (status === SUCCESS) {
        window.open(payload.certificateUrl, '_blank');
      } else {
        store.dispatch(showToast(`Something went wrong while fetching certificate.`));
      }
    } catch (e) {
      console.error(e);
      store.dispatch(showToast(`Something went wrong while fetching certificate.`));
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    setLoading(true);
    try {
      if (selectedCertificates.length > MAX_CERTIFICATES_DOWNLOAD_LIMIT) {
        showToastToUser(
          SNACK_BAR_ERROR,
          `Maximum ${MAX_CERTIFICATES_DOWNLOAD_LIMIT} certificates can be downloaded at once`
        );
        setLoading(false);
        return;
      }

      const { payload, status } = await downloadCertificatesInCombinedPDF(
        selectedCertificates.map((certificate) => certificate.id)
      );
      if (status === SUCCESS) {
        const filename = generateCertificateFilename();
        downloadBlobFile(convertBase64ToBlob(payload, 'application/pdf'), filename);
        setLoading(false);
      } else {
        setLoading(false);
        const { errorDetails } = payload;
        showToastToUser(SNACK_BAR_ERROR, errorDetails);
      }
    } catch (error) {
      setLoading(false);
      showToastToUser(SNACK_BAR_ERROR, error);
    }
  };

  function generateCertificateFilename() {
    const username = userDetails?.username;
    const sanitizedUsername = username.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
    const timestamp = new Date().toISOString().replace(/[-:]/g, '').replace('T', '-').slice(0, 13);
    
    return `certificates_${sanitizedUsername}_${timestamp}.pdf`;
  }

  const showToastToUser = (type, msg) => {
    store.dispatch(changeToastType(type, msg));
    store.dispatch(showToast(msg));
  };

  return {
    tableOptions,
    columns,
    certificatesData,
    isLoading,
    selectedCertificates,
    setCertificates,
    handleDownload,
  };
};
